import React, { useEffect, useState } from 'react';
import {
    Card,
    Button,
    Space,
    Badge,
    Typography,
    Descriptions,
    Spin,
    Alert,
    Modal,
    Input,
    Form
} from 'antd';
import {
    SafetyOutlined,
    CheckCircleOutlined,
    KeyOutlined,
    PoweroffOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
    getMFAStatus,
    disableMFA,
    openSetupModal,
    selectMFAEnabled,
    selectMFAMethod,
    selectMFAEnabledAt,
    selectMFALoading,
    selectMFAError
} from '../../redux/slices/mfaSlice';
import MFASetupModal from './MFASetupModal';
import BackupCodesModal from './BackupCodesModal';

const { Title, Text, Paragraph } = Typography;

const MFAManagementCard = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // Redux state
    const mfaEnabled = useSelector(selectMFAEnabled);
    const mfaMethod = useSelector(selectMFAMethod);
    const mfaEnabledAt = useSelector(selectMFAEnabledAt);
    const loading = useSelector(selectMFALoading);
    const error = useSelector(selectMFAError);

    // Local state
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
    const [disabling, setDisabling] = useState(false);

    // Load MFA status on component mount
    useEffect(() => {
        dispatch(getMFAStatus());
    }, [dispatch]);

    const handleEnableMFA = () => {
        dispatch(openSetupModal());
    };

    const handleOpenDisableModal = () => {
        setShowDisableModal(true);
        form.resetFields();
    };

    const handleCloseDisableModal = () => {
        setShowDisableModal(false);
        form.resetFields();
    };

    const handleDisableMFA = async () => {
        try {
            const values = await form.validateFields();
            setDisabling(true);

            const result = await dispatch(disableMFA(values.password));

            if (result.type.endsWith('/fulfilled')) {
                setShowDisableModal(false);
                form.resetFields();
                // Refresh MFA status
                dispatch(getMFAStatus());
            }
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setDisabling(false);
        }
    };

    const handleViewBackupCodes = () => {
        setShowBackupCodesModal(true);
    };

    const handleCloseBackupCodesModal = () => {
        setShowBackupCodesModal(false);
    };

    if (loading && !mfaEnabled && mfaMethod === null) {
        return (
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" tip="Loading MFA status..." />
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card
                title={
                    <Space>
                        <SafetyOutlined style={{ color: '#2E5A87', fontSize: '20px' }} />
                        <span>Multi-Factor Authentication</span>
                    </Space>
                }
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    marginTop: '24px'
                }}
            >
                {error && (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {!mfaEnabled ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Space size="middle" style={{ marginBottom: '12px' }}>
                                <Text strong style={{ fontSize: '16px' }}>Status:</Text>
                                <Badge status="default" text="Disabled" />
                            </Space>

                            <Paragraph style={{ color: '#666', marginBottom: '16px' }}>
                                Add an extra layer of security to your account with two-factor
                                authentication using an authenticator app. This helps protect
                                your account even if someone knows your password.
                            </Paragraph>

                            <Alert
                                message="Enhanced Security Recommended"
                                description="Protect your account and patient data by enabling MFA. It only takes 2 minutes to set up."
                                type="info"
                                showIcon
                                icon={<SafetyOutlined />}
                                style={{ marginBottom: '16px' }}
                            />
                        </div>

                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={handleEnableMFA}
                            size="large"
                            style={{
                                backgroundColor: '#2E5A87',
                                borderColor: '#2E5A87'
                            }}
                        >
                            Enable Multi-Factor Authentication
                        </Button>
                    </Space>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Space size="middle" style={{ marginBottom: '16px' }}>
                                <Text strong style={{ fontSize: '16px' }}>Status:</Text>
                                <Badge
                                    status="success"
                                    text={<Text strong style={{ color: '#43A047' }}>Enabled ✓</Text>}
                                />
                            </Space>

                            <Descriptions column={1} size="small" bordered>
                                <Descriptions.Item label="Method">
                                    <Space>
                                        <SafetyOutlined style={{ color: '#2E5A87' }} />
                                        <Text>Authenticator App (TOTP)</Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Enabled On">
                                    {mfaEnabledAt
                                        ? moment(mfaEnabledAt).format('DD/MM/YYYY [at] h:mm A')
                                        : 'Unknown'}
                                </Descriptions.Item>
                            </Descriptions>

                            <Alert
                                message="Your account is protected"
                                description="You'll need to enter a code from your authenticator app when signing in."
                                type="success"
                                showIcon
                                icon={<CheckCircleOutlined />}
                                style={{ marginTop: '16px' }}
                            />
                        </div>

                        <Space size="middle">
                            <Button
                                icon={<KeyOutlined />}
                                onClick={handleViewBackupCodes}
                                size="large"
                            >
                                View Backup Codes
                            </Button>
                            <Button
                                danger
                                icon={<PoweroffOutlined />}
                                onClick={handleOpenDisableModal}
                                size="large"
                            >
                                Disable MFA
                            </Button>
                        </Space>
                    </Space>
                )}
            </Card>

            {/* MFA Setup Modal */}
            <MFASetupModal />

            {/* Backup Codes Modal */}
            {showBackupCodesModal && (
                <BackupCodesModal
                    visible={showBackupCodesModal}
                    onClose={handleCloseBackupCodesModal}
                />
            )}

            {/* Disable MFA Confirmation Modal */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#E53935' }} />
                        <span>Disable Two-Factor Authentication</span>
                    </Space>
                }
                open={showDisableModal}
                onCancel={handleCloseDisableModal}
                footer={null}
                width={500}
                centered
                destroyOnClose
            >
                <Space direction="vertical" size="large" style={{ width: '100%', padding: '20px 0' }}>
                    <Alert
                        message="Warning"
                        description="Disabling MFA will make your account less secure. You will no longer need an authenticator app to log in."
                        type="warning"
                        showIcon
                    />

                    <Paragraph>
                        To confirm, please enter your password:
                    </Paragraph>

                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                                { required: true, message: 'Please enter your password' }
                            ]}
                        >
                            <Input.Password
                                placeholder="Enter your password"
                                size="large"
                                autoFocus
                            />
                        </Form.Item>
                    </Form>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCloseDisableModal}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                onClick={handleDisableMFA}
                                loading={disabling}
                            >
                                Disable MFA
                            </Button>
                        </Space>
                    </div>
                </Space>
            </Modal>
        </>
    );
};

export default MFAManagementCard;
